import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Send, Lock, ShieldCheck, AlertTriangle, Loader2, Pin, PinOff, Sparkles, Trash2, BadgeCheck, Image as ImageIcon, ImagePlay, X, Forward, Pencil, Gift, Sticker, Coins, Bot, Crown, Users, MessagesSquare, Clock, Check, Mic, Eye, Reply, Phone, Video, Globe } from "lucide-react";
import { ComposeExtras } from "@/components/chat/RichMessages";
import { BabelNudge } from "@/components/chat/BabelNudge";
import { useAutoTranslate } from "@/hooks/useAutoTranslate";
import { looksForeign, targetLang, isNudgeDismissed, dismissNudge } from "@/lib/babel";
import { uploadFileInChunks, validateFile, validateMedia, FILE_ACCEPT, AUDIO_ACCEPT, VIDEO_ACCEPT } from "@/lib/fileUpload";
import { blobToBase64, analyzeAudio } from "@/lib/voice";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck, TrustBand } from "@/components/common/TrustBadge";
import { OfficialBadge } from "@/components/common/OfficialBadge";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";
import { isOfficialUser } from "@/lib/official";
import { dayLabel, errToText } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CipherLockDialog } from "@/components/CipherLockDialog";
import { useCall } from "@/context/CallContext";
import { GroupCipherDialog } from "@/components/chat/GroupCipherDialog";
import { TipDialog } from "@/components/chat/TipDialog";
import { CryptoSendDialog } from "@/components/crypto/CryptoSendDialog";
import { StickerPicker } from "@/components/chat/StickerPicker";
import { EmojiButton } from "@/components/chat/EmojiButton";
import { GifPicker } from "@/components/chat/GifPicker";
import { SystemMessage } from "@/components/chat/SystemMessage";
import { MessageBubble, imgSrc } from "@/components/chat/MessageBubble";
import { SmartReplies } from "@/components/chat/SmartReplies";
import { ShieldExplainDialog } from "@/components/chat/ShieldExplainDialog";
import { GroupInfoDrawer } from "@/components/chat/GroupInfoDrawer";
import { MICROTX_ENABLED, CRYPTO_ENABLED } from "@/config";
import { useChatCipher } from "@/lib/crypto/useCipherLock";
import { encryptPayload, decryptPayload, getGroupRoster, getDilithiumInfo, onKeyArrival } from "@/lib/crypto/cipherlock";
import { getVerified, setVerified as persistVerified, getMemberVerified, setMemberVerified, getGroupCodeState, setGroupCodeVerified } from "@/lib/crypto/verify";
import { setE2eePreview } from "@/lib/crypto/previewStore";

async function fileToJpegBase64(file, maxDim = 1080, quality = 0.7) {
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(new Error("read"));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new window.Image();
    im.onload = () => res(im);
    im.onerror = () => rej(new Error("decode"));
    im.src = dataUrl;
  });
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) {
    const s = maxDim / Math.max(width, height);
    width = Math.round(width * s);
    height = Math.round(height * s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality).split(",")[1];
}

const DISAPPEAR_OPTS = [
  { v: 0, label: "Off" },
  { v: 3600, label: "1 hour" },
  { v: 28800, label: "8 hours" },
  { v: 86400, label: "24 hours" },
  { v: 604800, label: "7 days" },
];

// Short label for the message being quoted in the reply strip / bubble quote.
export function replyPreviewText(m) {
  if (!m) return "";
  if (m.deleted) return "Deleted message";
  if (m.text) return m.text;
  if (m.type === "voice" || m.voice) return "🎤 Voice note";
  if (m.type === "sticker") return "Sticker";
  const mime = m.file?.mime || "";
  if (m.image || mime.startsWith("image/")) return "📷 Photo";
  if (mime.startsWith("audio/")) return "🎵 Audio";
  if (mime.startsWith("video/")) return "🎬 Video";
  if (m.file) return `📎 ${m.file.name || "Attachment"}`;
  if (m.e2ee) return "Encrypted message";
  return "Message";
}

export default function ChatThread({ conversationId, onBack, extraHeaderActions, headerBadge, banner, bloqId, canEnableCipher, pinnedMessage, canPinMessages = false, allowForward = true, initialDraft = null }) {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const { startCall } = useCall();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [storyReplyCtx, setStoryReplyCtx] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [summary, setSummary] = useState(null);
  const [cipherOpen, setCipherOpen] = useState(false);
  const [groupCipherOpen, setGroupCipherOpen] = useState(false);
  const [verifiedMap, setVerifiedMap] = useState({});
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeStale, setCodeStale] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [forwardFor, setForwardFor] = useState(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [explainText, setExplainText] = useState(null); // Shield "why risky?" dialog target
  const [bloqDraft, setBloqDraft] = useState(null); // { loading, reply } — private @bloq draft
  const [editFor, setEditFor] = useState(null); // message being edited (plaintext only)
  const [editText, setEditText] = useState("");
  const [plain, setPlain] = useState({}); // messageId -> decrypted payload {text,image} (undefined=pending, null=no key)
  const [pendingImage, setPendingImage] = useState(null); // raw base64 jpeg awaiting send
  const [uploadPct, setUploadPct] = useState(null); // 0-100 while a file attachment uploads
  const [lightbox, setLightbox] = useState(null);
  const [viewOnce, setViewOnce] = useState(false); // send the pending image as view-once
  const [replyTo, setReplyTo] = useState(null); // message being replied to (v1.2.2 reply UI)
  const [disappearOpen, setDisappearOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recStreamRef = useRef(null);
  const recSecsRef = useRef(0);

  const { data, isLoading } = useQuery({
    queryKey: ["messages", conversationId],
    queryFn: () => api.getMessages(conversationId),
    enabled: !!conversationId,
  });

  const conv = data?.conversation;
  const isCircle = conv?.type === "circle";
  const isGroup = conv?.type === "group"; // Circle = small group chat
  const messages = useMemo(() => data?.messages || [], [data?.messages]);
  const otherId = conv?.other?.id;
  const official = !isCircle && !isGroup && isOfficialUser(conv?.other);
  const navigate = useNavigate();
  const { data: peerCreatorData } = useQuery({
    queryKey: ["peer-creator-tiers", otherId],
    queryFn: () => api.creatorTiers(otherId),
    enabled: !isCircle && !isGroup && !!otherId,
    retry: false,
    staleTime: 60000,
  });
  const peerHasTiers = !isCircle && !isGroup && peerCreatorData?.is_creator && (peerCreatorData?.tiers || []).some((t) => t.active);

  const { cipher, refreshCipher, enableGroupCipher } = useChatCipher(user?.id, conversationId, {
    enabled: !!conv && !isGroup,
    mode: isCircle ? "group" : "dm",
    bloqId,
  });
  const cipherActive = cipher?.state === "active" && !!cipher.sessionId;
  const [enablingCipher, setEnablingCipher] = useState(false);
  const [verified, setVerifiedState] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isCircle && cipherActive && cipher.fingerprint && user?.id) {
      getVerified(user.id, conversationId, cipher.fingerprint).then((v) => {
        if (!cancelled) setVerifiedState(v);
      });
    } else {
      setVerifiedState(false);
    }
    return () => {
      cancelled = true;
    };
  }, [isCircle, cipherActive, cipher?.fingerprint, conversationId, user?.id]);

  // Trust signal: did THIS device's Cipher Lock identity get quantum-seeded key material?
  // (Always false while the QRNG backend is the mock source — badge only shows for real QeM entropy.)
  const [quantumSeeded, setQuantumSeeded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (cipherActive && user?.id) {
      getDilithiumInfo(user.id)
        .then((i) => { if (!cancelled) setQuantumSeeded(!!i.quantumSeeded); })
        .catch(() => {});
    } else {
      setQuantumSeeded(false);
    }
    return () => { cancelled = true; };
  }, [cipherActive, user?.id]);


  const markVerified = async () => {
    if (!cipher?.fingerprint) return;
    await persistVerified(user.id, conversationId, cipher.fingerprint);
    setVerifiedState(true);
    toast.success("Contact verified");
  };

  // Group Cipher Lock verification roster (Phase 2b) — loaded when the group dialog is open + active.
  const { data: groupRoster, refetch: refetchRoster, isFetching: rosterLoading } = useQuery({
    queryKey: ["group-cipher-roster", bloqId, cipher?.sessionId],
    queryFn: () => getGroupRoster(user.id, bloqId),
    enabled: !!(isCircle && cipherActive && bloqId && user?.id),
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    let cancelled = false;
    const roster = groupRoster?.roster || [];
    if (!isCircle || !roster.length || !user?.id) {
      setVerifiedMap({});
      return;
    }
    Promise.all(
      roster
        .filter((m) => m.fp && m.id !== user.id)
        .map(async (m) => [m.id, await getMemberVerified(user.id, bloqId, m.id, m.fp)]),
    ).then((pairs) => {
      if (!cancelled) setVerifiedMap(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [groupRoster, isCircle, bloqId, user?.id]);

  const verifyMember = async (memberId, fp) => {
    await setMemberVerified(user.id, bloqId, memberId, fp);
    setVerifiedMap((prev) => ({ ...prev, [memberId]: true }));
    toast.success("Member verified");
  };

  // Whole-Circle confirmation of the shared group safety code → drives the "Verified secure" header pill.
  useEffect(() => {
    let cancelled = false;
    const sn = groupRoster?.safetyNumber;
    if (!isCircle || !sn || !user?.id) {
      setCodeVerified(false);
      setCodeStale(false);
      return;
    }
    getGroupCodeState(user.id, bloqId, sn).then((st) => {
      if (!cancelled) {
        setCodeVerified(st.verified);
        setCodeStale(st.stale);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [groupRoster?.safetyNumber, isCircle, bloqId, user?.id]);

  const verifyGroupCode = async () => {
    const sn = groupRoster?.safetyNumber;
    if (!sn) return;
    await setGroupCodeVerified(user.id, bloqId, sn);
    setCodeVerified(true);
    setCodeStale(false);
    // Persist the confirmation server-side (mobile parity) — best-effort, never blocks the local UI.
    if (bloqId) api.confirmGroupCipherCode(bloqId, sn).catch(() => {});
    toast.success("Circle verified secure");
  };

  const enableCipher = async () => {
    setEnablingCipher(true);
    try {
      await enableGroupCipher();
      toast.success("Cipher Lock enabled for this bloq");
      qc.invalidateQueries({ queryKey: ["bloq", bloqId] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
    } catch (e) {
      toast.error(errToText(e, "Couldn't enable Cipher Lock"));
    } finally {
      setEnablingCipher(false);
    }
  };

  useEffect(() => {
    setPinned(!!data?.conversation?.pinned);
  }, [data?.conversation?.pinned, conversationId]);

  const sendMut = useMutation({
    mutationFn: ({ text: t, image, viewOnce: vo, replyTo: rt }) => {
      if (cipherActive) {
        const blob = encryptPayload(cipher.sessionId, { text: t, image: image || null });
        if (blob) return api.sendEncryptedMessage(conversationId, blob, { replyTo: rt });
      }
      return api.sendMessage(conversationId, t, image || null, { viewOnce: vo, replyTo: rt });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      setReplyTo(null);
    },
    onError: (e) => {
      const code = e?.response?.data?.code || e?.response?.data?.detail?.code;
      if (code === "upgrade_required") toast.error("HD images need BLOQ+ — try a smaller image or upgrade.");
      else toast.error("Couldn't send message.");
    },
  });

  const sendVoiceMut = useMutation({
    mutationFn: ({ voiceB64, voiceDur, voiceWave }) => api.sendVoice(conversationId, voiceB64, voiceDur, voiceWave),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
    },
    onError: () => toast.error("Couldn't send voice note."),
  });

  const disappearMut = useMutation({
    mutationFn: (seconds) => api.setDisappearing(conversationId, seconds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      setDisappearOpen(false);
      toast.success("Disappearing timer updated");
    },
    onError: () => toast.error("Couldn't update the disappearing timer."),
  });

  const sendStickerMut = useMutation({
    mutationFn: (stickerId) => api.sendSticker(conversationId, stickerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.error : null;
      const st = e?.response?.status;
      if (code === "not_owned") toast.error("You don't own this sticker pack.");
      else if (st === 404 || st === 405) toast.info("Stickers aren't live yet.");
      else toast.error("Couldn't send sticker.");
    },
  });

  const reactMut = useMutation({
    mutationFn: ({ messageId, emoji }) => api.reactMessage(conversationId, messageId, emoji),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages", conversationId] }),
    onError: () => toast.error("Couldn't react"),
  });

  const sendGifMut = useMutation({
    mutationFn: (gif) => api.sendGif(conversationId, gif, { replyTo: replyTo?.id }),
    onSuccess: () => {
      setReplyTo(null);
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
    },
    onError: (e) => toast.error(errToText(e, "Couldn't send GIF.")),
  });

  const deleteMut = useMutation({
    mutationFn: (messageId) => api.deleteMessage(conversationId, messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      toast.success("Message deleted");
    },
    onError: () => toast.error("Couldn't delete message"),
  });

  const pinMut = useMutation({
    mutationFn: () => api.pinChat(conversationId),
    onSuccess: (res) => {
      setPinned(!!res?.pinned);
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      toast.success(res?.pinned ? "Chat pinned" : "Chat unpinned");
    },
    onError: () => toast.error("Couldn't pin chat"),
  });

  const summaryMut = useMutation({
    mutationFn: () => api.chatSummary(conversationId),
    onSuccess: (res) => setSummary(res?.summary || "No summary available."),
    onError: () => toast.error("Couldn't summarize"),
  });

  const deleteChatMut = useMutation({
    mutationFn: () => api.deleteChat(conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      toast.success("Conversation deleted");
      setConfirmDelete(false);
      onBack?.();
    },
    onError: () => toast.error("Couldn't delete conversation"),
  });

  const pinMessageMut = useMutation({
    mutationFn: (messageId) => api.pinBloqMessage(bloqId, messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bloq", bloqId] });
      toast.success("Message pinned");
    },
    onError: (e) => toast.error(errToText(e, "Couldn't pin message")),
  });

  const unpinMessageMut = useMutation({
    mutationFn: () => api.unpinBloqMessage(bloqId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bloq", bloqId] });
      toast.success("Message unpinned");
    },
    onError: () => toast.error("Couldn't unpin message"),
  });

  const forwardMut = useMutation({
    mutationFn: ({ messageId, toChatId }) => api.forwardMessage(conversationId, messageId, toChatId),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["messages", vars.toChatId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      toast.success("Message forwarded");
      setForwardFor(null);
    },
    onError: () => toast.error("Couldn't forward message"),
  });

  const invalidateThread = () => {
    qc.invalidateQueries({ queryKey: ["messages", conversationId] });
    qc.invalidateQueries({ queryKey: ["chats"] });
    qc.invalidateQueries({ queryKey: ["bloqs"] });
  };

  const editMut = useMutation({
    mutationFn: ({ messageId, text: t }) => api.editMessage(conversationId, messageId, t),
    onSuccess: () => {
      invalidateThread();
      setEditFor(null);
      toast.success("Message edited");
    },
    onError: (e) => toast.error(errToText(e, "Couldn't edit message")),
  });

  const pollMut = useMutation({
    mutationFn: (payload) => api.createPoll(conversationId, payload),
    onSuccess: invalidateThread,
    onError: () => toast.error("Couldn't create poll"),
  });

  const voteMut = useMutation({
    mutationFn: ({ messageId, option }) => api.votePoll(conversationId, messageId, option),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages", conversationId] }),
    onError: () => toast.error("Couldn't record vote"),
  });

  const checklistMut = useMutation({
    mutationFn: (payload) => api.createChecklist(conversationId, payload),
    onSuccess: invalidateThread,
    onError: () => toast.error("Couldn't create checklist"),
  });

  const toggleMut = useMutation({
    mutationFn: ({ messageId, item }) => api.toggleChecklist(conversationId, messageId, item),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["messages", conversationId] }),
    onError: () => toast.error("Couldn't update checklist"),
  });

  const contactMut = useMutation({
    mutationFn: (payload) => api.shareContact(conversationId, payload),
    onSuccess: invalidateThread,
    onError: () => toast.error("Couldn't share contact"),
  });

  const { data: forwardChats } = useQuery({
    queryKey: ["chats"],
    queryFn: api.listChats,
    enabled: !!forwardFor,
  });

  useEffect(() => {
    return subscribe((evt) => {
      if (evt?.type === "message.new" && evt.message?.conversation_id === conversationId) {
        qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      } else if (evt?.type === "message.updated" && evt.conversation_id === conversationId && evt.message_id && evt.patch) {
        // Live in-place upgrade (e.g. a link preview finished fetching ~1s after send).
        qc.setQueryData(["messages", conversationId], (old) => {
          if (!old?.messages) return old;
          return { ...old, messages: old.messages.map((m) => (m.id === evt.message_id ? { ...m, ...evt.patch } : m)) };
        });
      }
    });
  }, [subscribe, conversationId, qc]);

  useEffect(() => {
    if (conversationId) {
      api.markRead(conversationId).then(() => {
        qc.invalidateQueries({ queryKey: ["chats"] });
        qc.invalidateQueries({ queryKey: ["bloqs"] });
        if (bloqId) qc.invalidateQueries({ queryKey: ["bloq-topics", bloqId] });
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [data?.messages?.length]);

  // Decrypt any Cipher Lock (e2ee) messages once session keys are available. Locked bubbles
  // (null = key not on this device yet) are retried whenever a key arrives (wrap / heal, cc_20).
  const [keyEpoch, setKeyEpoch] = useState(0);
  const retryLockedRef = useRef(false);
  useEffect(() => onKeyArrival(() => {
    retryLockedRef.current = true;
    setKeyEpoch((e) => e + 1);
    refreshCipher({ healOnly: true });
  }), [refreshCipher]);
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const retryLocked = retryLockedRef.current;
    retryLockedRef.current = false;
    (async () => {
      const updates = {};
      for (const m of messages) {
        if (m.e2ee && (!(m.id in plain) || (retryLocked && plain[m.id] === null))) updates[m.id] = await decryptPayload(user.id, m.e2ee);
      }
      if (!cancelled && Object.keys(updates).length) setPlain((p) => ({ ...p, ...updates }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, user?.id, cipher?.sessionId, keyEpoch]);

  // Cache a device-local preview flag for the latest message so the chat list can
  // show "Encrypted photo" vs "Encrypted message" (only a boolean is stored, no plaintext).
  // ── Babel (cc_24): per-chat auto-translate. Effective state comes from conversation.auto_translate
  // (an explicit per-chat toggle always wins over the profile default auto_translate_all).
  const autoTranslateOn = !!conv?.auto_translate;
  const { translations: autoTranslations, paused: babelPaused } = useAutoTranslate({ enabled: autoTranslateOn, messages, plain, user, conversationId });
  const babelMut = useMutation({
    mutationFn: () => (isCircle && bloqId ? api.toggleBloqAutoTranslate(bloqId) : api.toggleChatAutoTranslate(conversationId)),
    onSuccess: (res) => {
      qc.setQueryData(["messages", conversationId], (old) => (old?.conversation ? { ...old, conversation: { ...old.conversation, auto_translate: !!res?.auto_translate } } : old));
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      if (bloqId) qc.invalidateQueries({ queryKey: ["bloqs"] });
      toast.success(res?.auto_translate ? "Babel on — incoming messages are translated for you" : "Babel off");
    },
    onError: (e) => toast.error(errToText(e, "Couldn't toggle auto-translate")),
  });
  const [nudgeHidden, setNudgeHidden] = useState(false);
  const showBabelNudge = useMemo(() => {
    if (autoTranslateOn || nudgeHidden || !conversationId || !user?.id || isNudgeDismissed(conversationId)) return false;
    const tgt = targetLang(user);
    const incoming = messages.filter((m) => m.sender_id !== user.id && !m.deleted).slice(-10);
    const foreign = incoming.filter((m) => looksForeign(m.e2ee ? plain[m.id]?.text : m.text, tgt)).length;
    return foreign >= 2;
  }, [autoTranslateOn, nudgeHidden, conversationId, user, messages, plain]);
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || !conversationId) return;
    if (last.e2ee) {
      const p = plain[last.id];
      if (p && typeof p === "object") setE2eePreview(conversationId, { img: !!p.image, at: last.created_at });
    } else {
      setE2eePreview(conversationId, { img: !!last.image, at: last.created_at });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages.length, plain]);

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if ((!t && !pendingImage) || sendMut.isPending) return;
    // "@bloq …" → private in-chat AI draft (never posted to the peer).
    const m = t.match(/^@bloq\b[:,]?\s*([\s\S]*)$/i);
    if (m) {
      setText("");
      askBloq(m[1].trim());
      return;
    }
    const image = pendingImage;
    const vo = viewOnce;
    const rt = replyTo?.id || null;
    setText("");
    setPendingImage(null);
    setViewOnce(false);
    setStoryReplyCtx(null);
    sendMut.mutate({ text: t, image, viewOnce: vo, replyTo: rt });
  };

  // Auto-growing composer (parity with mobile: grows to ~140px, then scrolls). Enter sends, Shift+Enter = newline.
  const composerRef = useRef(null);
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, 140);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > 140 ? "auto" : "hidden";
  }, [text]);
  const onComposerKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      submit(e);
    }
  };
  const insertEmoji = (emoji) => {
    const el = composerRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = `${text.slice(0, start)}${emoji}${text.slice(end)}`;
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + emoji.length;
      try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
    });
  };

  // Story-reply deep-link: opened via /app/chats/:id with { storyReply } nav state → prefill the composer.
  useEffect(() => {
    if (initialDraft?.text) {
      setText(initialDraft.text);
      setStoryReplyCtx({ ownerName: initialDraft.ownerName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft]);

  // @bloq — ask the Assistant to draft/fact-check a reply, privately. Shares the Assistant thread
  // + daily quota (per Mobile). Recent thread text is passed as transient context.
  const askBloq = async (q) => {
    setBloqDraft({ loading: true, reply: null });
    try {
      const ctx = replyContext.length
        ? `Conversation so far:\n${replyContext.map((r) => `${r.sender === "me" ? "Me" : (header.name || "Them")}: ${r.text}`).join("\n")}\n\n`
        : "";
      const ask = q || "Draft a good reply to the last message.";
      // thread:"draft" — quota still applies but the turn is NOT persisted to the main Assistant history.
      const res = await api.assistantSend(`${ctx}${ask}`.trim(), { thread: "draft" });
      setBloqDraft({ loading: false, reply: res?.reply || "" });
    } catch (e) {
      setBloqDraft(null);
      if (e?.response?.status === 429) toast.error("Daily AI limit reached — try again tomorrow.");
      else toast.error("BLOQ couldn't draft a reply.");
    }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    try {
      setPendingImage(await fileToJpegBase64(file));
    } catch {
      toast.error("Couldn't process that image.");
    }
  };

  const onPickAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateFile(file);
    if (err) return toast.error(err);
    setUploadPct(0);
    try {
      await uploadFileInChunks(conversationId, file, setUploadPct);
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      toast.success("File sent");
    } catch (err2) {
      toast.error(errToText(err2, "Couldn't send file"));
    } finally {
      setUploadPct(null);
    }
  };

  // Audio & video ride the same chunked pipeline (audio → kind "file", video → kind "video").
  const onPickMedia = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateMedia(file, kind);
    if (err) return toast.error(err);
    setUploadPct(0);
    try {
      await uploadFileInChunks(conversationId, file, setUploadPct);
      qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      toast.success(`${kind === "audio" ? "Audio" : "Video"} sent`);
    } catch (err2) {
      toast.error(errToText(err2, `Couldn't send ${kind}`));
    } finally {
      setUploadPct(null);
    }
  };

  // Voice notes — record via MediaRecorder, then send base64 + duration + a downsampled waveform.
  const startRec = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      recChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data?.size) recChunksRef.current.push(ev.data); };
      mr.onstop = () => finishRec(mr._send);
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecSecs(0);
      recSecsRef.current = 0;
      recTimerRef.current = setInterval(() => { recSecsRef.current += 1; setRecSecs(recSecsRef.current); }, 1000);
    } catch {
      toast.error("Microphone permission is needed to record a voice note.");
    }
  };

  const stopRec = (send) => {
    clearInterval(recTimerRef.current);
    const mr = mediaRecorderRef.current;
    if (mr) { mr._send = send; if (mr.state !== "inactive") mr.stop(); }
    setRecording(false);
  };

  const finishRec = async (send) => {
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
    recStreamRef.current = null;
    const chunks = recChunksRef.current;
    recChunksRef.current = [];
    const secs = recSecsRef.current;
    if (!send) return;
    const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
    if (blob.size < 400) return toast.error("That recording was too short.");
    try {
      const voiceB64 = await blobToBase64(blob);
      const { duration, wave } = await analyzeAudio(blob);
      sendVoiceMut.mutate({ voiceB64, voiceDur: Math.max(1, Math.round(duration || secs)), voiceWave: wave });
    } catch {
      toast.error("Couldn't process the recording.");
    }
  };

  useEffect(() => () => {
    clearInterval(recTimerRef.current);
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);


  const header = isCircle
    ? { name: conv?.circle?.name, sub: `${conv?.circle?.member_count || 0} members`, emoji: conv?.circle?.emoji }
    : isGroup
    ? { name: conv?.name, sub: `${conv?.member_count || 0} members`, isGroup: true, iconB64: conv?.icon_b64 }
    : { name: conv?.other?.display_name, sub: conv?.other ? `@${conv.other.username}` : "", user: conv?.other };

  // Smart replies (Lane C) — build privacy-safe transient context from the last few
  // visible messages (decrypted in-memory for Cipher Lock threads; never re-sent encrypted).
  const replyContext = useMemo(
    () =>
      messages
        .slice(-8)
        .map((m) => {
          let t = "";
          if (m.e2ee) {
            const p = plain[m.id];
            t = p && typeof p === "object" ? p.text || "" : "";
          } else {
            t = m.text || "";
          }
          return t ? { sender: m.sender_id === user?.id ? "me" : "them", text: t } : null;
        })
        .filter(Boolean),
    [messages, plain, user?.id],
  );
  const lastMsg = messages[messages.length - 1];
  const showSmartReplies = !isCircle && !isGroup && !!lastMsg && lastMsg.sender_id !== user?.id && replyContext.length > 0;

  let lastDay = null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-bloq-bg">
      <div className="flex shrink-0 items-center gap-3 border-b border-white/8 bg-bloq-s1/60 px-4 py-3 backdrop-blur-xl">
        {onBack && (
          <button onClick={onBack} className="rounded-full p-1 text-bloq-muted hover:text-white md:hidden" data-testid="chat-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {isCircle ? (
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2 text-xl">{header.emoji}</div>
        ) : isGroup ? (
          header.iconB64 ? (
            <button onClick={() => setGroupInfoOpen(true)} data-testid="group-header-avatar"><img src={header.iconB64} alt="" className="h-11 w-11 rounded-2xl object-cover" /></button>
          ) : (
            <button onClick={() => setGroupInfoOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint" data-testid="group-header-avatar"><MessagesSquare className="h-5 w-5" /></button>
          )
        ) : header.user ? (
          <Avatar user={header.user} size="sm" />
        ) : (
          <div className="h-10 w-10 rounded-2xl bg-bloq-s2" />
        )}
        <div className={cn("min-w-0 flex-1", isGroup && "cursor-pointer")} onClick={isGroup ? () => setGroupInfoOpen(true) : undefined} data-testid={isGroup ? "group-header-info" : undefined}>
          <div className="flex items-center gap-1.5">
            <span className="truncate font-semibold">{header.name || "Conversation"}</span>
            {!isCircle && header.user?.verified && <VerifiedCheck size={14} />}
            {!isCircle && <TopConnectorCrown match={header.user} iconOnly />}
            {official && <OfficialBadge className="ml-0.5" size={13} />}
            {isCircle && cipherActive && codeVerified && (
              <span
                className="flex items-center gap-1 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bloq-mint"
                title="You verified this Circle's Cipher Lock safety code"
                data-testid="circle-verified-secure-pill"
              >
                <ShieldCheck className="h-3 w-3" /> Verified secure
              </span>
            )}
            {isCircle && cipherActive && !codeVerified && codeStale && (
              <button
                onClick={() => { setGroupCipherOpen(true); refetchRoster(); }}
                className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400 transition-colors hover:bg-amber-500/25"
                title="The group safety code changed — re-verify"
                data-testid="circle-reverify-pill"
              >
                <AlertTriangle className="h-3 w-3" /> Re-verify
              </button>
            )}
            {!isCircle && verified && (
              <span title="Cipher Lock safety number verified" data-testid="chat-verified-badge">
                <BadgeCheck className="h-3.5 w-3.5 text-bloq-mint" />
              </span>
            )}
            {!isCircle && header.user?.score_band && <span className="hidden sm:inline-flex"><TrustBand band={header.user.score_band} /></span>}
            {headerBadge}
          </div>
          <div className="truncate text-xs text-bloq-muted">{header.sub}</div>
        </div>
        <div className="flex min-w-0 max-w-[58%] items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:max-w-none sm:shrink-0 sm:overflow-visible" data-testid="chat-header-actions">
          {extraHeaderActions}
          {peerHasTiers && (
            <button
              onClick={() => navigate(`/app/creators/${otherId}`)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-purple"
              data-testid="chat-membership-btn"
              title="Support with a membership"
            >
              <Crown className="h-4 w-4" />
            </button>
          )}
          {!isCircle && !isGroup && otherId && (
            <>
              <button
                onClick={() => startCall(conversationId, { video: false, peer: conv?.other })}
                className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
                data-testid="call-voice-btn"
                title="Voice call"
              >
                <Phone className="h-4 w-4" />
              </button>
              <button
                onClick={() => startCall(conversationId, { video: true, peer: conv?.other })}
                className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
                data-testid="call-video-btn"
                title="Video call"
              >
                <Video className="h-4 w-4" />
              </button>
            </>
          )}
          {!isCircle && !isGroup && (
            <button
              onClick={() => setCipherOpen(true)}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/8",
                cipher?.state === "active" ? "text-bloq-mint" : "text-bloq-muted hover:text-bloq-mint",
              )}
              data-testid="cipherlock-btn"
              title={cipher?.state === "active" ? "Cipher Lock active" : "Cipher Lock status"}
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
          )}
          {isCircle && cipher && cipher.state !== "off" && (
            <button
              onClick={() => { setGroupCipherOpen(true); refetchRoster(); }}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/8",
                cipherActive ? "text-bloq-mint" : "text-bloq-muted hover:text-bloq-mint",
              )}
              data-testid="group-cipherlock-btn"
              title={cipherActive ? "Cipher Lock — verify members" : "Cipher Lock status"}
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
          )}
          {isGroup && (
            <button
              onClick={() => setGroupInfoOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
              data-testid="group-info-btn"
              title="Circle info & members"
            >
              <Users className="h-4 w-4" />
            </button>
          )}
          {!isCircle && (
            <button
              onClick={() => setDisappearOpen(true)}
              className={cn("flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/8", conv?.disappearing_seconds ? "text-bloq-mint" : "text-bloq-muted hover:text-white")}
              data-testid="chat-disappearing-btn"
              title="Disappearing messages"
            >
              <Clock className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => babelMut.mutate()}
            disabled={babelMut.isPending}
            className={cn("flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/8", autoTranslateOn ? "text-bloq-blue" : "text-bloq-muted hover:text-white")}
            data-testid="chat-babel-btn"
            title={autoTranslateOn ? (babelPaused ? "Babel paused — daily translate limit reached" : "Babel auto-translate on") : "Auto-translate this chat (Babel)"}
          >
            {babelMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className={cn("h-4 w-4", babelPaused && "opacity-50")} />}
          </button>
          <button
            onClick={() => summaryMut.mutate()}
            disabled={summaryMut.isPending}
            className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint disabled:opacity-50"
            data-testid="chat-summary-btn"
            title="AI thread summary"
          >
            {summaryMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          </button>
          <button
            onClick={() => pinMut.mutate()}
            disabled={pinMut.isPending}
            className={cn("flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/8", pinned ? "text-bloq-mint" : "text-bloq-muted hover:text-white")}
            data-testid="chat-pin-btn"
            title={pinned ? "Unpin chat" : "Pin chat"}
          >
            {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
          {!isCircle && !isGroup && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-destructive/15 hover:text-destructive"
              data-testid="chat-delete-btn"
              title="Delete conversation"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {banner}

      {isCircle && pinnedMessage?.message_id && (
        <div className="flex items-center gap-2 border-b border-white/8 bg-bloq-s2/60 px-4 py-2" data-testid="bloq-pinned-banner">
          <Pin className="h-3.5 w-3.5 shrink-0 text-bloq-mint" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold text-bloq-mint">
              Pinned{pinnedMessage.by_name ? ` by ${pinnedMessage.by_name}` : ""}
            </div>
            <div className="truncate text-xs text-bloq-muted" data-testid="bloq-pinned-preview">
              {pinnedMessage.preview || "Pinned message"}
            </div>
          </div>
          {canPinMessages && (
            <button
              onClick={() => unpinMessageMut.mutate()}
              disabled={unpinMessageMut.isPending}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-white disabled:opacity-50"
              data-testid="bloq-unpin-btn"
              title="Unpin message"
            >
              {unpinMessageMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PinOff className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      )}
      {showBabelNudge && (
        <BabelNudge busy={babelMut.isPending} onEnable={() => babelMut.mutate()} onDismiss={() => { dismissNudge(conversationId); setNudgeHidden(true); }} />
      )}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-5">
        <div
          className={cn(
            "mx-auto mb-2 flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px]",
            cipherActive
              ? "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint"
              : "border-white/8 bg-bloq-s2 text-bloq-muted",
          )}
          data-testid="e2ee-pill"
        >
          <Lock className="h-3 w-3 text-bloq-mint" />
          {cipherActive
            ? "Cipher Lock active · post-quantum E2EE"
            : cipher?.state === "waiting"
              ? "Waiting for group key…"
              : cipher?.state === "pending"
                ? "Securing this chat…"
                : "End-to-end encrypted"}
        </div>

        {isCircle && canEnableCipher && (cipher?.state === "available" || cipher?.state === "waiting") && (
          <button
            onClick={enableCipher}
            disabled={enablingCipher}
            className="mx-auto mb-2 flex w-fit items-center gap-1.5 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-3.5 py-1.5 text-xs font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15 disabled:opacity-60"
            data-testid="enable-group-cipher-btn"
          >
            {enablingCipher ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            {cipher?.state === "waiting" ? "Re-key Cipher Lock on this device" : "Enable Cipher Lock for this bloq"}
          </button>
        )}

        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          messages.map((m) => {
            const day = dayLabel(m.created_at);
            const showDay = day !== lastDay;
            lastDay = day;
            return (
              <div key={m.id} className="space-y-3">
                {showDay && (
                  <div className="my-3 text-center text-[11px] font-medium uppercase tracking-wider text-bloq-muted">{day}</div>
                )}
                {m.type === "system" && !m.e2ee ? (
                  <SystemMessage msg={m} bloqId={bloqId} />
                ) : (
                <MessageBubble
                  msg={m}
                  isOwn={m.sender_id === user?.id}
                  isCircle={isCircle || isGroup}
                  members={conv?.members}
                  currentUserId={user?.id}
                  otherId={otherId}
                  me={user}
                  peer={conv?.other}
                  official={official}
                  payload={m.e2ee ? (m.id in plain ? plain[m.id] : undefined) : { text: m.text ?? "", image: m.image ?? null }}
                  autoTranslation={autoTranslations[m.id] || null}
                  onImageClick={setLightbox}
                  onReact={(messageId, emoji) => reactMut.mutate({ messageId, emoji })}
                  onDelete={(messageId) => deleteMut.mutate(messageId)}
                  onForward={allowForward ? (messageId) => setForwardFor(messageId) : undefined}
                  onReply={(message) => setReplyTo(message)}
                  onPin={isCircle && canPinMessages ? (messageId) => pinMessageMut.mutate(messageId) : undefined}
                  onEdit={(message) => { setEditFor(message); setEditText(message.text || ""); }}
                  onVote={(messageId, option) => voteMut.mutate({ messageId, option })}
                  onToggle={(messageId, item) => toggleMut.mutate({ messageId, item })}
                  onExplain={(t) => setExplainText(t)}
                  pickerOpen={pickerFor === m.id}
                  setPicker={setPickerFor}
                  chatId={conversationId}
                />
                )}
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {bloqDraft && (
        <div className="mx-4 mt-2 rounded-2xl border border-bloq-mint/25 bg-bloq-mint/[0.06] p-3" data-testid="bloq-draft">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-bloq-mint">
            <Bot className="h-3.5 w-3.5" /> BLOQ draft <span className="font-normal text-bloq-muted">· only visible to you</span>
          </div>
          {bloqDraft.loading ? (
            <div className="flex items-center gap-2 text-sm text-bloq-muted" data-testid="bloq-draft-loading">
              <Loader2 className="h-4 w-4 animate-spin text-bloq-mint" /> Drafting…
            </div>
          ) : (
            <>
              <p className="whitespace-pre-wrap break-words text-sm text-white/90" data-testid="bloq-draft-text">{bloqDraft.reply}</p>
              <div className="mt-2 flex items-center gap-2">
                <button type="button" onClick={() => { setText(bloqDraft.reply); setBloqDraft(null); }} className="rounded-full bloq-gradient px-3 py-1.5 text-xs font-bold text-bloq-bg" data-testid="bloq-draft-use">Use as reply</button>
                <button type="button" onClick={() => setBloqDraft(null)} className="rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted hover:text-white" data-testid="bloq-draft-dismiss">Dismiss</button>
              </div>
            </>
          )}
        </div>
      )}

      {text.trim().toLowerCase().startsWith("@bloq") && !bloqDraft && (
        <div className="mx-4 mt-2 flex items-center gap-1.5 rounded-full bg-bloq-mint/10 px-3 py-1.5 text-[11px] text-bloq-mint" data-testid="bloq-armed-hint">
          <Bot className="h-3.5 w-3.5" /> Press send — BLOQ will draft a private reply, just for you.
        </div>
      )}

      {showSmartReplies && !bloqDraft && (
        <SmartReplies key={lastMsg.id} messages={replyContext} onPick={(t) => setText(t)} />
      )}

      <form onSubmit={submit} className="flex shrink-0 flex-col gap-2 border-t border-white/8 bg-bloq-s1/60 px-4 py-3 backdrop-blur-xl" data-testid="chat-composer">
        {storyReplyCtx && (
          <div className="flex items-center gap-2 rounded-xl border-l-2 border-bloq-purple bg-bloq-purple/[0.08] px-3 py-2" data-testid="chat-story-reply-ctx">
            <Sparkles className="h-4 w-4 shrink-0 text-bloq-purple" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold text-bloq-purple">Replying to {storyReplyCtx.ownerName ? `${storyReplyCtx.ownerName}'s` : "their"} story</div>
              <div className="truncate text-xs text-bloq-muted">Hit send to reply privately</div>
            </div>
            <button type="button" onClick={() => setStoryReplyCtx(null)} className="shrink-0 rounded-full p-1 text-bloq-muted hover:text-white" data-testid="chat-story-reply-cancel" title="Cancel">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {replyTo && (
          <div className="flex items-center gap-2 rounded-xl border-l-2 border-bloq-mint bg-white/[0.04] px-3 py-2" data-testid="chat-reply-preview">
            <Reply className="h-4 w-4 shrink-0 text-bloq-mint" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold text-bloq-mint">
                Replying to {replyTo.sender_id === user?.id ? "yourself" : (replyTo.sender?.display_name || header.name || "message")}
              </div>
              <div className="truncate text-xs text-bloq-muted" data-testid="chat-reply-preview-text">{replyPreviewText(replyTo)}</div>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} className="shrink-0 rounded-full p-1 text-bloq-muted hover:text-white" data-testid="chat-reply-cancel" title="Cancel reply">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {pendingImage && (
          <div className="flex items-center gap-2" data-testid="chat-image-preview">
            <div className="relative">
              <img src={imgSrc(pendingImage)} alt="Attachment preview" className="h-16 w-16 rounded-xl object-cover" />
              <button
                type="button"
                onClick={() => setPendingImage(null)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-bloq-bg text-bloq-muted ring-1 ring-white/15 hover:text-white"
                data-testid="chat-image-remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <span className={cn("flex items-center gap-1 text-[11px]", cipherActive ? "text-bloq-mint" : "text-bloq-muted")}>
              {cipherActive ? <Lock className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
              {cipherActive ? "Image will be sent encrypted" : "Image ready to send"}
            </span>
            {!cipherActive && (
              <button
                type="button"
                onClick={() => setViewOnce((v) => !v)}
                className={cn("ml-auto flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors", viewOnce ? "border-bloq-mint/50 bg-bloq-mint/15 text-bloq-mint" : "border-white/12 text-bloq-muted hover:text-white")}
                data-testid="chat-viewonce-toggle"
                title="Recipient can view this image only once"
              >
                <Eye className="h-3 w-3" /> View once
              </button>
            )}
            {cipherActive && (
              <span className="ml-auto text-[10px] text-bloq-muted" data-testid="chat-viewonce-cipher-note">View-once unavailable in encrypted chats</span>
            )}
          </div>
        )}
        {uploadPct !== null && (
          <div className="flex items-center gap-2" data-testid="file-upload-progress">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-bloq-mint" />
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-bloq-mint transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
            <span className="text-[11px] text-bloq-muted">{uploadPct}%</span>
          </div>
        )}
        {recording && (
          <div className="flex items-center gap-3 rounded-full border border-destructive/30 bg-destructive/10 px-4 py-2" data-testid="voice-recording-bar">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-destructive" />
            <span className="text-sm font-semibold text-destructive">Recording… {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, "0")}</span>
            <button type="button" onClick={() => stopRec(false)} className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-bloq-muted hover:text-white" data-testid="voice-cancel-btn" title="Cancel recording"><X className="h-4 w-4" /></button>
            <button type="button" onClick={() => stopRec(true)} className="flex h-8 w-8 items-center justify-center rounded-full bloq-gradient text-bloq-bg" data-testid="voice-send-btn" title="Send voice note"><Send className="h-4 w-4" /></button>
          </div>
        )}
        <div className={cn("flex items-end gap-2", recording && "hidden")}>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="chat-image-input" />
          <input ref={attachInputRef} type="file" accept={FILE_ACCEPT} onChange={onPickAttachment} className="hidden" data-testid="chat-attach-file-input" />
          <input ref={audioInputRef} type="file" accept={AUDIO_ACCEPT} onChange={onPickMedia("audio")} className="hidden" data-testid="chat-audio-input" />
          <input ref={videoInputRef} type="file" accept={VIDEO_ACCEPT} onChange={onPickMedia("video")} className="hidden" data-testid="chat-video-input" />
          <ComposeExtras
            onPoll={(payload) => pollMut.mutate(payload)}
            onChecklist={(payload) => checklistMut.mutate(payload)}
            onContact={(payload) => contactMut.mutate(payload)}
            onAttachFile={() => attachInputRef.current?.click()}
            onAttachAudio={() => audioInputRef.current?.click()}
            onAttachVideo={() => videoInputRef.current?.click()}
            busy={pollMut.isPending || checklistMut.isPending || contactMut.isPending || uploadPct !== null}
            mobileItems={[
              { key: "image", label: "Photo", icon: ImageIcon, onClick: () => fileInputRef.current?.click() },
              { key: "gif", label: "GIF", icon: ImagePlay, onClick: () => setGifOpen(true) },
              ...(MICROTX_ENABLED ? [{ key: "sticker", label: "Sticker", icon: Sticker, onClick: () => setStickerOpen(true) }] : []),
              ...(MICROTX_ENABLED && !isCircle && otherId ? [{ key: "tip", label: "Send a tip", icon: Gift, onClick: () => setTipOpen(true) }] : []),
              ...(CRYPTO_ENABLED && !isCircle && otherId ? [{ key: "crypto", label: "Send crypto", icon: Coins, onClick: () => setCryptoOpen(true) }] : []),
            ]}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="hidden h-11 w-11 shrink-0 sm:flex items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
            data-testid="chat-attach-btn"
            title="Attach an image"
          >
            <ImageIcon className="h-5 w-5" />
          </button>
          {MICROTX_ENABLED && !isCircle && otherId && (
            <button
              type="button"
              onClick={() => setTipOpen(true)}
              className="hidden h-11 w-11 shrink-0 sm:flex items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
              data-testid="chat-tip-btn"
              title="Send a tip"
            >
              <Gift className="h-5 w-5" />
            </button>
          )}
          {CRYPTO_ENABLED && !isCircle && otherId && (
            <button
              type="button"
              onClick={() => setCryptoOpen(true)}
              className="hidden h-11 w-11 shrink-0 sm:flex items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
              data-testid="chat-crypto-btn"
              title="Send crypto"
            >
              <Coins className="h-5 w-5" />
            </button>
          )}
          {MICROTX_ENABLED && (
            <button
              type="button"
              onClick={() => setStickerOpen(true)}
              className="hidden h-11 w-11 shrink-0 sm:flex items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
              data-testid="chat-sticker-btn"
              title="Send a sticker"
            >
              <Sticker className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setGifOpen(true)}
            className="hidden h-11 w-11 shrink-0 sm:flex items-center justify-center rounded-full text-[10px] font-black tracking-wide text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
            data-testid="chat-gif-btn"
            title="Send a GIF"
          >
            GIF
          </button>
          <button
            type="button"
            onClick={startRec}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
            data-testid="chat-voice-btn"
            title="Record a voice note"
          >
            <Mic className="h-5 w-5" />
          </button>
          <EmojiButton
            onPick={insertEmoji}
            onClose={() => composerRef.current?.focus()}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
          />
          <textarea
            ref={composerRef}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onComposerKeyDown}
            placeholder="Write a message…"
            className="max-h-[140px] min-h-[44px] min-w-0 flex-1 resize-none overflow-y-hidden rounded-2xl border border-white/10 bg-bloq-bg px-4 py-[11px] text-[15px] leading-[22px] text-white outline-none placeholder:text-bloq-muted focus:border-bloq-mint/40"
            data-testid="chat-message-input"
          />
          <button
            type="submit"
            disabled={(!text.trim() && !pendingImage) || sendMut.isPending}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bloq-gradient text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
            data-testid="chat-send-btn"
          >
            {sendMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </form>

      {messages.length > 0 && messages[messages.length - 1]?.scam_intent && (
        <div className="flex items-center gap-2 bg-destructive/15 px-4 py-2 text-xs text-destructive">
          <AlertTriangle className="h-4 w-4" /> BLOQ Shield flagged a recent message as risky.
        </div>
      )}

      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="chat-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Sparkles className="h-4 w-4 text-bloq-mint" /> Thread summary
            </DialogTitle>
            <DialogDescription className="text-bloq-muted">AI-generated recap of this conversation.</DialogDescription>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/90">{summary}</div>
        </DialogContent>
      </Dialog>

      <Dialog open={disappearOpen} onOpenChange={setDisappearOpen}>
        <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="disappearing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Clock className="h-4 w-4 text-bloq-mint" /> Disappearing messages</DialogTitle>
            <DialogDescription className="text-bloq-muted">New messages disappear for everyone after the timer. Existing messages aren't affected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {DISAPPEAR_OPTS.map((o) => {
              const current = (conv?.disappearing_seconds || 0) === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => disappearMut.mutate(o.v)}
                  disabled={disappearMut.isPending}
                  className={cn("flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm transition-colors disabled:opacity-60", current ? "border-bloq-mint/50 bg-bloq-mint/10 text-bloq-mint" : "border-white/10 text-white hover:bg-white/5")}
                  data-testid={`disappearing-opt-${o.v}`}
                >
                  {o.label}
                  {current && <Check className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-bloq-s1 p-2" data-testid="image-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>Shared image</DialogTitle>
            <DialogDescription>Full-size image preview</DialogDescription>
          </DialogHeader>
          {lightbox && <img src={lightbox} alt="Shared attachment" className="max-h-[80vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>

      {!isCircle && (
        <CipherLockDialog open={cipherOpen} onOpenChange={setCipherOpen} cipher={cipher} onRefresh={refreshCipher} verified={verified} onVerify={markVerified} quantumSeeded={quantumSeeded} chatId={conversationId} userId={user?.id} peerName={header.name} />
      )}
      {isCircle && (
        <GroupCipherDialog
          open={groupCipherOpen}
          onOpenChange={setGroupCipherOpen}
          cipher={cipher}
          roster={groupRoster?.roster || []}
          membersInfo={conv?.members || {}}
          verifiedMap={verifiedMap}
          safetyNumber={groupRoster?.safetyNumber}
          codeVerified={codeVerified}
          codeStale={codeStale}
          quantumSeeded={quantumSeeded}
          onVerifyCode={verifyGroupCode}
          onVerifyMember={verifyMember}
          onRefresh={refetchRoster}
          loadingRoster={rosterLoading}
        />
      )}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="delete-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete conversation?</DialogTitle>
            <DialogDescription className="text-bloq-muted">
              This removes the conversation from your list. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5"
              data-testid="delete-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteChatMut.mutate()}
              disabled={deleteChatMut.isPending}
              className="rounded-full font-bold"
              data-testid="delete-chat-confirm"
            >
              {deleteChatMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!forwardFor} onOpenChange={(o) => !o && setForwardFor(null)}>
        <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="forward-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Forward className="h-4 w-4 text-bloq-mint" /> Forward to…
            </DialogTitle>
            <DialogDescription className="text-bloq-muted">Choose a conversation to forward this message to.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto">
            {(forwardChats || []).filter((c) => c.id !== conversationId).length === 0 ? (
              <p className="py-6 text-center text-sm text-bloq-muted">No other conversations to forward to.</p>
            ) : (
              (forwardChats || [])
                .filter((c) => c.id !== conversationId)
                .map((c) => (
                  <button
                    key={c.id}
                    onClick={() => forwardMut.mutate({ messageId: forwardFor, toChatId: c.id })}
                    disabled={forwardMut.isPending}
                    className="flex w-full items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:bg-white/5 disabled:opacity-50"
                    data-testid={`forward-target-${c.id}`}
                  >
                    {c.other ? <Avatar user={c.other} size="sm" /> : <div className="h-10 w-10 rounded-2xl bg-bloq-s2" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{c.other?.display_name || "Conversation"}</div>
                      {c.other?.username && <div className="truncate text-xs text-bloq-muted">@{c.other.username}</div>}
                    </div>
                    {forwardMut.isPending && forwardMut.variables?.toChatId === c.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-bloq-mint" />
                    )}
                  </button>
                ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="edit-message-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Pencil className="h-4 w-4 text-bloq-mint" /> Edit message
            </DialogTitle>
            <DialogDescription className="text-bloq-muted">Only your text messages can be edited.</DialogDescription>
          </DialogHeader>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            maxLength={4000}
            className="w-full resize-none rounded-xl border border-white/12 bg-bloq-bg px-3 py-2 text-[15px] text-white outline-none placeholder:text-bloq-muted focus:border-bloq-mint/40"
            data-testid="edit-message-input"
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setEditFor(null)}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5"
              data-testid="edit-message-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => editMut.mutate({ messageId: editFor.id, text: editText.trim() })}
              disabled={editMut.isPending || !editText.trim() || editText.trim() === (editFor?.text || "")}
              className="rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
              data-testid="edit-message-save"
            >
              {editMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {MICROTX_ENABLED && !isCircle && (
        <TipDialog open={tipOpen} onOpenChange={setTipOpen} recipient={conv?.other} context={{ type: "dm", id: conversationId }} />
      )}
      {CRYPTO_ENABLED && !isCircle && (
        <CryptoSendDialog open={cryptoOpen} onOpenChange={setCryptoOpen} recipient={conv?.other} context={{ type: "dm", id: conversationId }} />
      )}
      {MICROTX_ENABLED && (
        <StickerPicker open={stickerOpen} onOpenChange={setStickerOpen} onSend={(s) => sendStickerMut.mutate(s.id)} />
      )}
      <GifPicker open={gifOpen} onOpenChange={setGifOpen} onSend={(g) => sendGifMut.mutate(g)} />
      <ShieldExplainDialog open={!!explainText} text={explainText || ""} onOpenChange={(o) => !o && setExplainText(null)} />
      {isGroup && <GroupInfoDrawer chatId={conversationId} open={groupInfoOpen} onOpenChange={setGroupInfoOpen} />}
    </div>
  );
}
